import type {
  ExpectedActionDraft,
  InteractionType,
} from './expectedAction.interface.js';

/** An empty expected-action draft with no selections. */
export function createEmptyDraft(): ExpectedActionDraft {
  return {
    engagementInputs: [],
    interactionTypes: [],
    associatedScenarios: [],
    associatedCustomizationAsks: [],
  };
}

/** True when the given engagement type is currently selected. */
export function hasEngagementType(
  draft: ExpectedActionDraft,
  typeId: string,
): boolean {
  return draft.engagementInputs.some((entry) => entry.type === typeId);
}

/** Toggle an engagement type on (empty inputs) or off. */
export function toggleEngagementType(
  draft: ExpectedActionDraft,
  typeId: string,
): ExpectedActionDraft {
  if (hasEngagementType(draft, typeId)) {
    return {
      ...draft,
      engagementInputs: draft.engagementInputs.filter(
        (entry) => entry.type !== typeId,
      ),
    };
  }
  return {
    ...draft,
    engagementInputs: [...draft.engagementInputs, { type: typeId, inputs: {} }],
  };
}

/** Read a single input value for an engagement type (empty string if unset). */
export function getEngagementInput(
  draft: ExpectedActionDraft,
  typeId: string,
  inputName: string,
): string {
  const entry = draft.engagementInputs.find((item) => item.type === typeId);
  return entry?.inputs[inputName] ?? '';
}

/** Set a single input value for an engagement type. */
export function setEngagementInput(
  draft: ExpectedActionDraft,
  typeId: string,
  inputName: string,
  value: string,
): ExpectedActionDraft {
  return {
    ...draft,
    engagementInputs: draft.engagementInputs.map((entry) =>
      entry.type === typeId
        ? { ...entry, inputs: { ...entry.inputs, [inputName]: value } }
        : entry,
    ),
  };
}

/** Toggle a reactive/proactive interaction-type tag. */
export function toggleInteractionType(
  draft: ExpectedActionDraft,
  interactionType: InteractionType,
): ExpectedActionDraft {
  const isActive = draft.interactionTypes.includes(interactionType);
  return {
    ...draft,
    interactionTypes: isActive
      ? draft.interactionTypes.filter((item) => item !== interactionType)
      : [...draft.interactionTypes, interactionType],
  };
}

/** Add a scenario association (no-op when already present or blank). */
export function addScenario(
  draft: ExpectedActionDraft,
  scenarioId: string,
): ExpectedActionDraft {
  const trimmed = scenarioId.trim();
  if (trimmed === '' || draft.associatedScenarios.includes(trimmed)) {
    return draft;
  }
  return {
    ...draft,
    associatedScenarios: [...draft.associatedScenarios, trimmed],
  };
}

/** Remove a scenario association. */
export function removeScenario(
  draft: ExpectedActionDraft,
  scenarioId: string,
): ExpectedActionDraft {
  return {
    ...draft,
    associatedScenarios: draft.associatedScenarios.filter(
      (item) => item !== scenarioId,
    ),
  };
}

/** Add a customization-ask association (no-op when already present or blank). */
export function addCustomizationAsk(
  draft: ExpectedActionDraft,
  ask: string,
): ExpectedActionDraft {
  const trimmed = ask.trim();
  if (trimmed === '' || draft.associatedCustomizationAsks.includes(trimmed)) {
    return draft;
  }
  return {
    ...draft,
    associatedCustomizationAsks: [
      ...draft.associatedCustomizationAsks,
      trimmed,
    ],
  };
}

/** Remove a customization-ask association. */
export function removeCustomizationAsk(
  draft: ExpectedActionDraft,
  ask: string,
): ExpectedActionDraft {
  return {
    ...draft,
    associatedCustomizationAsks: draft.associatedCustomizationAsks.filter(
      (item) => item !== ask,
    ),
  };
}
